import { RegistrationApiError } from './errors';
/**
 * registration — API.
 *
 * Pre-auth endpoints (no JWT exists yet), so this uses raw fetch against the
 * backend instead of the authenticated `http` helper — the same approach as
 * backend-auth-provider.ts. Errors carry the backend message when available.
 */
import { getBackendApiUrl } from '../../config/environment';
import type { BackendLoginResponse } from '../../services/authentication/backend-auth-provider';
import type {
  CheckEmailResponse,
  CompleteSignupPayload,
  RequestCodeResponse,
  VerifyCodeResponse,
} from './types';

/**
 * Error thrown for non-2xx registration responses, keeping status + backend payload.
 *
 * The fields are declared and assigned explicitly rather than as constructor
 * parameter properties: `erasableSyntaxOnly` is on in tsconfig, and parameter
 * properties are the one class feature that cannot be erased to plain JS.
 */

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${getBackendApiUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  const data = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const message =
      (data && typeof data.message === 'string' && data.message) ||
      `Request failed with status ${response.status}`;
    throw new RegistrationApiError(response.status, message, data);
  }
  return data as T;
}

export const registrationApi = {
  checkEmail: (email: string) =>
    post<CheckEmailResponse>('/registration/check-email', { email }),

  requestCode: (email: string, locale?: string, captchaToken?: string) =>
    post<RequestCodeResponse>('/registration/request-code', { email, locale, captchaToken }),

  verifyCode: (email: string, code: string) =>
    post<VerifyCodeResponse>('/registration/verify-code', { email, code }),

  completeSignup: (payload: CompleteSignupPayload) =>
    post<BackendLoginResponse>('/registration/complete-signup', payload),

  /** Emails a 6-digit sign-in access code — 200 even for unknown emails */
  requestLoginCode: (email: string, locale?: string, captchaToken?: string) =>
    post<RequestCodeResponse>('/registration/request-login-code', { email, locale, captchaToken }),

  /** Signs in by typing the emailed access code — auto-login response */
  loginWithCode: (email: string, code: string) =>
    post<BackendLoginResponse>('/registration/login-with-code', { email, code }),

  /** Accepts a co-guardian invite link token — auto-login response */
  loginWithLink: (token: string) =>
    post<BackendLoginResponse>('/registration/login-with-link', { token }),
};
